// TutorsProgramWorkloadBar.jsx
import React, { useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import { TrendingUp } from "@mui/icons-material";
import { BarChart } from "@mui/x-charts/BarChart";

/**
 * Props:
 *  - data: Array<{ first_name, last_name, tutor_id, program_name, id (program_id), duration_total, session_count }>
 *
 * Notes:
 *  - Assumes `duration_total` is in MINUTES from the SQL; converted to HOURS for display.
 *  - Horizontal bars: Y = tutor, X = total hours; stacked by program.
 */
export default function TutorsProgramWorkloadBar({ data }) {
  const theme = useTheme();
  const rows = Array.isArray(data) ? data : [];

  // Build tutor labels and aggregate totals (to sort tutors by total duration desc)
  const {
    yLabels,               // tutors list (sorted)
    programList,           // unique program names (stable order)
    matrixHoursByProgram,  // program -> number[] aligned to yLabels (hours)
  } = useMemo(() => {
    const tutorKey = (r) =>
      `${(r.first_name || "").trim()} ${(r.last_name || "").trim()}`.trim();

    // Collect tutors and totals
    const tutorTotals = new Map();           // key -> totalMinutes
    const tutors = new Map();                // key -> tutor_id
    const programsSet = new Map();           // program_name -> true (to preserve first-seen order)

    // Map (tutor_key, program_name) -> minutes
    const tpMinutes = new Map();

    for (const r of rows) {
      const key = tutorKey(r) || `Tutor #${r.tutor_id}`;
      tutors.set(key, r.tutor_id);
      programsSet.set(r.program_name, true);

      const minutes = Number(r.duration_sum) / Number(r.session_count) || 0;
      const tpKey = `${key}||${r.program_name}`;
      tpMinutes.set(tpKey, (tpMinutes.get(tpKey) || 0) + minutes);
      tutorTotals.set(key, (tutorTotals.get(key) || 0) + minutes);
    }

    // Sort tutors by total minutes desc
    const yLabels = Array.from(tutors.keys()).sort(
      (a, b) => (tutorTotals.get(b) || 0) - (tutorTotals.get(a) || 0)
    );

    // Stable program order by first-seen
    const programList = Array.from(programsSet.keys());

    // Build series data (convert minutes -> hours with 1 decimal)
    const matrixHoursByProgram = {};
    for (const prog of programList) {
      matrixHoursByProgram[prog] = yLabels.map((tutor) => {
        const minutes = tpMinutes.get(`${tutor}||${prog}`) || 0;
        const hours = minutes / 60;
        return Math.round(hours * 10) / 10; // 1 decimal
      });
    }

    return { yLabels, programList, matrixHoursByProgram };
  }, [rows]);

  // Palette for programs (stable)
  const colorAt = (idx) => {
    const palette = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.info.main,
      theme.palette.warning.main,
      theme.palette.error.main,
    ];
    return palette[idx % palette.length];
  };

  // Build x-charts series array (stacked by program)
  const series = useMemo(() => {
    return programList.map((prog, i) => ({
      label: prog,
      data: matrixHoursByProgram[prog],
      color: colorAt(i),
      valueFormatter: (v) => (v == null ? "" : `${v} h`),
      stack: "total", // stack programs together
    }));
  }, [programList, matrixHoursByProgram]);

  const chartSetting = { height: 360 };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header (kept consistent with your style) */}
      <Box sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ display: "flex", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Tutor workload by program
            </Typography>
            <Typography variant="body2" color="text.secondary">
              &nbsp;Total teaching time (hours), stacked by program
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      {yLabels.length === 0 ? (
        <Box sx={{ height: 200, display: "grid", placeItems: "center", color: "text.secondary" }}>
          <Typography variant="body2">No data to display</Typography>
        </Box>
      ) : (
        <BarChart
          layout="horizontal" // horizontal bars
          yAxis={[{ scaleType: "band", data: yLabels, label: "" }]}
          xAxis={[{ label: "Total Duration (hours)" }]}
          series={series}
          slotProps={{
            legend: {
              direction: "row",
              position: { vertical: "bottom", horizontal: "middle" },
            },
          }}
          margin={{ top: 24, right: 24, left: 100, bottom: 56 }}
          {...chartSetting}
        />
      )}
    </Box>
  );
}